import React, { useState } from 'react';
// Import selected icons from lucide-react
import {
  LayoutDashboard,
  ArrowRightLeft,
  Users,
  FileText,
  ShieldCheck,
  ChevronsLeft,
  Landmark,
  Archive,
  type LucideIcon // Import LucideIcon type for props
} from 'lucide-react';
// *** NEW: Import NavLink from react-router-dom ***
import { NavLink } from 'react-router-dom';

interface NavItemProps {
  // Use LucideIcon type for the icon prop
  Icon: LucideIcon;
  label: string;
  // active?: boolean; // REMOVED: active state will be handled by NavLink
  isCollapsed?: boolean;
  path: string; // *** NEW: Add path prop ***
}

// Update NavItem to accept and render an Icon component and use NavLink
const NavItem: React.FC<NavItemProps> = ({ Icon, label, isCollapsed = false, path }) => {
  const [isHovering, setIsHovering] = useState(false); // *** NEW: Hover state ***
  const activeBg = '#eef2ff'; // Light indigo background for active item
  const activeColor = '#4f46e5'; // Indigo color for active text/icon
  const hoverBg = '#f1f5f9'; // Light gray on hover
  const textColor = '#334155'; // Default text color
  const inactiveIconColor = '#64748b'; // Icon color when inactive

  return (
    <li style={{
      listStyle: 'none',
      margin: '4px 0',
    }}>
      {/* *** NEW: Use NavLink instead of a tag *** */}
      <NavLink
         to={path} // Use path prop for destination
         style={({ isActive }) => ({ // Style function to access isActive state
           display: 'flex',
           alignItems: 'center',
           padding: isCollapsed ? '10px 0' : '10px 12px',
           justifyContent: isCollapsed ? 'center' : 'flex-start',
           borderRadius: '6px',
           textDecoration: 'none',
           fontSize: '14px',
           fontWeight: isActive ? '600' : '500', // Style based on isActive
           color: isActive ? activeColor : textColor,
           // *** UPDATED: Background depends on isActive and isHovering state ***
           backgroundColor: isActive ? activeBg : (isHovering ? hoverBg : 'transparent'),
           transition: 'background-color 0.2s ease, padding 0.3s ease, justify-content 0.3s ease, color 0.2s ease, font-weight 0.2s ease',
           overflow: 'hidden',
           whiteSpace: 'nowrap',
         })}
         onMouseEnter={() => setIsHovering(true)} // *** NEW: Set hover state ***
         onMouseLeave={() => setIsHovering(false)} // *** NEW: Clear hover state ***
         end // Add end prop for exact path matching on base route ('/')
      >
        {({ isActive }) => ( // Render function to access isActive for icon styling
          <>
            <Icon
              size={20} // Consistent icon size
              color={isActive ? activeColor : inactiveIconColor} // Style based on isActive
              style={{
                marginRight: isCollapsed ? '0' : '12px', // Remove margin when collapsed
                flexShrink: 0, // Prevent icon from shrinking
                transition: 'margin-right 0.3s ease, color 0.2s ease',
              }}
              strokeWidth={isActive ? 2 : 1.5} // Make active icon slightly bolder
            />
            <span style={{
              marginLeft: '0', // Remove previous margin, Icon provides it now
              opacity: isCollapsed ? 0 : 1,
              transition: 'opacity 0.2s ease 0.1s',
              display: isCollapsed ? 'none' : 'inline',
            }}>
              {label}
            </span>
            <span style={{ display: 'none' }}> {/* Empty span to receive hover events when collapsed */}
            </span>
          </>
        )}
      </NavLink>
    </li>
  );
};

const Sidebar: React.FC = () => {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const toggleSidebar = () => setIsCollapsed(!isCollapsed);

  // Update navItems to use the imported Icon components and add paths
  const navItems = [
    { Icon: LayoutDashboard, label: 'Dashboard', path: '/' },
    { Icon: ArrowRightLeft, label: 'Transactions', path: '/transactions' },
    { Icon: Landmark, label: 'Payouts', path: '/payouts' },
    { Icon: Archive, label: 'Collections', path: '/collections' },
    { Icon: Users, label: 'Partners', path: '/partners' },
    { Icon: FileText, label: 'Reports', path: '/reports' },
    { Icon: ShieldCheck, label: 'Risk Management', path: '/risk-management' },
  ];

  return (
    <aside style={{
      width: isCollapsed ? '80px' : '260px',
      borderRight: '1px solid #e2e8f0',
      backgroundColor: '#ffffff',
      padding: isCollapsed ? '20px 8px' : '20px 16px',
      display: 'flex',
      flexDirection: 'column',
      height: '100vh',
      boxSizing: 'border-box',
      transition: 'width 0.3s ease, padding 0.3s ease',
    }}>
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: isCollapsed ? 'center' : 'space-between',
        marginBottom: '24px',
        paddingLeft: isCollapsed ? '0' : '12px',
        paddingRight: isCollapsed ? '0' : '4px',
        transition: 'padding-left 0.3s ease, padding-right 0.3s ease',
      }}>
        <h2 style={{
          fontSize: '20px',
          fontWeight: '600',
          color: '#1e293b',
          margin: 0,
          opacity: isCollapsed ? 0 : 1,
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          transition: 'opacity 0.2s ease 0.1s',
          display: isCollapsed ? 'none' : 'block',
        }}>Tabby Finance</h2>

        <button
          onClick={toggleSidebar}
          title={isCollapsed ? "Expand Sidebar" : "Collapse Sidebar"}
          style={{
            background: 'none',
            border: 'none',
            padding: '4px',
            borderRadius: '4px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#64748b',
            transition: 'background-color 0.2s ease',
          }}
          onMouseEnter={e => e.currentTarget.style.backgroundColor = '#f1f5f9'}
          onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}
        >
          <ChevronsLeft
            size={20}
            style={{
              transition: 'transform 0.3s ease',
              transform: isCollapsed ? 'rotate(180deg)' : 'rotate(0deg)',
            }}
          />
        </button>
      </div>

      <nav style={{ flexGrow: 1, overflow: 'hidden' }}>
        <ul style={{ padding: 0, margin: 0 }}>
          {navItems.map(item => (
            // Pass path and remove active prop
            <NavItem key={item.label} Icon={item.Icon} label={item.label} path={item.path} isCollapsed={isCollapsed} />
          ))}
        </ul>
      </nav>

      <div style={{
        marginTop: 'auto',
        paddingTop: '20px',
        borderTop: '1px solid #e2e8f0',
        display: 'flex',
        alignItems: 'center',
        paddingLeft: isCollapsed ? '0' : '12px',
        justifyContent: isCollapsed ? 'center' : 'flex-start',
        overflow: 'hidden',
        transition: 'padding-left 0.3s ease, justify-content 0.3s ease',
      }}>
          <img 
            src="/ahmed.png" // Use the image path relative to the public folder
            alt="User Avatar"
            style={{
              width: '36px',
              height: '36px',
              borderRadius: '50%',
              objectFit: 'cover', // Ensure the image covers the area well
              marginRight: isCollapsed ? '0' : '12px',
              flexShrink: 0,
              transition: 'margin-right 0.3s ease',
           }}/>
          <div style={{
            opacity: isCollapsed ? 0 : 1,
            transition: 'opacity 0.2s ease 0.1s',
            whiteSpace: 'nowrap',
            display: isCollapsed ? 'none' : 'block',
           }}>
            <div style={{ fontSize: '14px', fontWeight: '500', color: '#1e293b' }}>Ahmed Osama</div>
            <div style={{ fontSize: '12px', color: '#64748b' }}>ahmed.elaskary@tabby.ai</div>
          </div>
      </div>
    </aside>
  );
};

export default Sidebar; 